import React, { createContext, useCallback, useContext, useState } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { IconX } from '@tabler/icons-react';
import classNames from 'classnames';

type ToastOptions = {
	title?: string;
	description?: string;
	duration?: number; // ms
	variant: 'success' | 'error' | 'info';
};

type ToastContextValue = {
	showToast: (opts: ToastOptions) => void;
	showSuccess: (opts: Omit<ToastOptions, 'variant'>) => void;
	showError: (opts: Omit<ToastOptions, 'variant'>) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error('useToast must be used within ToastProvider');
	return ctx;
};

export const ToastProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
	const [toasts, setToasts] = useState<Array<{ id: string } & ToastOptions>>([]);

	const push = useCallback((opts: ToastOptions) => {
		const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
		setToasts((s) => [...s, { id, ...opts }]);
	}, []);

	const remove = useCallback((id: string) => {
		setToasts((s) => s.filter((t) => t.id !== id));
	}, []);

	const showSuccess = useCallback(
		(opts: Omit<ToastOptions, 'variant'>) => push({ ...opts, variant: 'success' }),
		[push]
	);
	const showError = useCallback((opts: Omit<ToastOptions, 'variant'>) => push({ ...opts, variant: 'error' }), [push]);

	return (
		<ToastContext.Provider value={{ showToast: push, showSuccess, showError }}>
			<RadixToast.Provider>
				{children}

				{toasts.map((t) => (
					<RadixToast.Root
						key={t.id}
						onOpenChange={(open) => {
							if (!open) remove(t.id);
						}}
						duration={t.duration ?? 5000}
						className={classNames(
							'relative w-80 max-w-full transform rounded-md border bg-white p-3 shadow-md transition-all duration-200 ease-out dark:bg-slate-800',
							'[data-state=closed]:translate-y-2 [data-state=closed]:opacity-0 [data-state=open]:translate-y-0 [data-state=open]:opacity-100'
						)}
					>
						<div
							className={classNames('flex flex-col gap-2 border-l-4 pl-2', {
								'border-emerald-500': t.variant === 'success',
								'border-red-500': t.variant === 'error'
							})}
						>
							{t.title && <RadixToast.Title className="font-semibold">{t.title}</RadixToast.Title>}
							{t.description && (
								<RadixToast.Description className="block text-sm text-slate-600 dark:text-slate-300">
									{t.description}
								</RadixToast.Description>
							)}
							<RadixToast.Close className="absolute top-1 right-1 p-2 text-xs" aria-label="Close">
								<IconX size={14} />
							</RadixToast.Close>
						</div>
					</RadixToast.Root>
				))}

				<RadixToast.Viewport className="fixed right-4 bottom-4 z-[9999] flex w-80 max-w-full flex-col gap-2 p-2" />
			</RadixToast.Provider>
		</ToastContext.Provider>
	);
};

export default ToastProvider;
